import { useLocalStorage } from "@src/appV2/lib/utils";

interface FeedbackEntry {
  provided: boolean;
  timestamp: number;
}

type FeedbackStorage = Record<string, FeedbackEntry>;

const STORAGE_KEY = "workplace-review-summary-feedback";
const MAX_ENTRIES = 500;

export function useWorkplaceSummaryFeedbackStorage(summaryId: string) {
  const [feedbackData, setFeedbackData] = useLocalStorage<FeedbackStorage>(STORAGE_KEY, {});

  const hasProvidedFeedback = feedbackData[summaryId]?.provided || false;

  const setHasProvidedFeedback = (provided: boolean) => {
    const currentEntries = Object.keys(feedbackData);

    // Cleanup old entries if we're at the limit and this is a new entry
    if (currentEntries.length >= MAX_ENTRIES && !feedbackData[summaryId]) {
      const sortedEntries = currentEntries
        .map((id) => ({ id, timestamp: feedbackData[id].timestamp }))
        .sort((a, b) => a.timestamp - b.timestamp);

      // Remove oldest 100 entries to make room
      const entriesToRemove = sortedEntries.slice(0, 100);
      const entriesToRemoveSet = new Set(entriesToRemove.map((entry) => entry.id));

      // Create new object without the entries to remove
      const cleanedData = Object.fromEntries(
        Object.entries(feedbackData).filter(([id]) => !entriesToRemoveSet.has(id))
      );

      setFeedbackData({
        ...cleanedData,
        [summaryId]: { provided, timestamp: Date.now() },
      });
    } else {
      setFeedbackData({
        ...feedbackData,
        [summaryId]: { provided, timestamp: Date.now() },
      });
    }
  };

  return [hasProvidedFeedback, setHasProvidedFeedback] as const;
}
